import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  ApiResponse,
  ExpenseClaimListItem,
  ExpenseClaimListResponse,
  ExpenseClaimVm,
  ExpenseClaimLineVm,
  ExpenseClaimLookupItem,
  ExpenseClaimSavePayload,
} from '@/api/types'

type ExpenseClaimLineFiles = Record<number, File | undefined>

function normalizeLookup(item: any): ExpenseClaimLookupItem {
  const percent =
    item.taxPercent ??
    item.TaxPercent ??
    item.taxRatePercent ??
    item.TaxRatePercent ??
    item.taxRate ??
    item.TaxRate

  const rate =
    item.taxRate ??
    item.TaxRate ??
    item.taxRatePercent ??
    item.TaxRatePercent ??
    item.taxPercent ??
    item.TaxPercent

  return {
    id: item.id ?? item.Id ?? item.taxRateId ?? item.TaxRateId ?? 0,
    name:
      item.name ??
      item.Name ??
      item.lookupNameEn ??
      item.LookupNameEn ??
      item.nameEn ??
      item.NameEn ??
      item.taxRateNameEn ??
      item.TaxRateNameEn,
    nameEn: item.nameEn ?? item.NameEn ?? item.lookupNameEn ?? item.LookupNameEn ?? item.taxRateNameEn ?? item.TaxRateNameEn,
    nameAr: item.nameAr ?? item.NameAr ?? item.lookupNameAr ?? item.LookupNameAr ?? item.taxRateNameAr ?? item.TaxRateNameAr,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn,
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr,
    taxPercent: percent,
    taxRate: rate,
  }
}

function normalizeListItem(row: any): ExpenseClaimListItem {
  return {
    id: row.id ?? row.Id ?? 0,
    claimDate: row.claimDate ?? row.ClaimDate ?? row.expenseDate ?? row.ExpenseDate ?? null,
    amount: row.amount ?? row.Amount ?? row.total ?? row.Total,
    detail: row.detail ?? row.Detail ?? row.details ?? row.Details,
    status: row.status ?? row.Status,
    statusName: row.statusName ?? row.StatusName,
    approved: row.approved ?? row.Approved ?? null,
    approvedDate: row.approvedDate ?? row.ApprovedDate ?? null,
    departmentId: row.departmentId ?? row.DepartmentId,
    departmentName: row.departmentName ?? row.DepartmentName,
    employeeId: row.employeeId ?? row.EmployeeId,
    employeeName: row.employeeName ?? row.EmployeeName,
    claimTypeId: row.claimTypeId ?? row.ClaimTypeId,
    claimTypeName: row.claimTypeName ?? row.ClaimTypeName,
  }
}

class ExpenseClaimService {
  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    formData.append(key, String(value))
  }

  private logOutboundPayload(
    action: 'save' | 'update',
    payload: ExpenseClaimSavePayload,
    expenseClaimLinesJson: string,
    lineFiles: ExpenseClaimLineFiles
  ) {
    if (import.meta.env.VITE_ENABLE_DEBUG !== 'true') return

    const { expenseClaimD, ...master } = payload
    console.log(`[ExpenseClaim] ${action} — master (multipart text fields)`, master)
    console.log(`[ExpenseClaim] ${action} — expenseClaimLinesJson (form field value)`, expenseClaimLinesJson)
    console.log(`[ExpenseClaim] ${action} — lines (parsed)`, expenseClaimD)
    console.log(
      `[ExpenseClaim] ${action} — line files`,
      Object.entries(lineFiles).map(([idx, f]) => ({
        index: Number(idx),
        file: f ? { name: f.name, size: f.size, type: f.type } : null,
      }))
    )
  }

  private debugFormData(label: string, formData: FormData) {
    // Only expand FormData in console when debug is enabled
    if (import.meta.env.VITE_ENABLE_DEBUG !== 'true') return

    const entries: Array<{ key: string; value: unknown }> = []
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        entries.push({
          key,
          value: { name: value.name, size: value.size, type: value.type },
        })
      } else {
        entries.push({ key, value })
      }
    }
    console.log(`[ExpenseClaim] ${label} — FormData entries`, entries)
  }

  private buildSaveFormData(payload: ExpenseClaimSavePayload, lineFiles: ExpenseClaimLineFiles = {}): FormData {
    const formData = new FormData()

    // Master fields (names must match backend DTO: ExpenseClaimVm)
    this.appendFormField(formData, 'Id', payload.id)
    this.appendFormField(formData, 'EmpId', payload.empId)
    this.appendFormField(formData, 'DepartmentId', payload.departmentId)
    this.appendFormField(formData, 'ClaimType', payload.claimType)
    this.appendFormField(formData, 'Title', payload.title)
    this.appendFormField(formData, 'SpendDate', payload.spendDate)
    this.appendFormField(formData, 'ClaimDate', payload.claimDate)
    this.appendFormField(formData, 'Amount', payload.amount)
    this.appendFormField(formData, 'BranchId', payload.branchId)
    this.appendFormField(formData, 'Details', payload.details)

    // Detail lines: send as JSON (API fallback parser reads expenseClaimLinesJson)
    const linesJson = (payload.expenseClaimD ?? []).map((l) => ({
      lineId: l.lineId ?? 0,
      invoiceDate: l.invoiceDate ?? null,
      description: l.description ?? '',
      price: l.price ?? 0,
      vatPercent: l.vatPercent ?? 0,
      vatAmount: l.vatAmount ?? 0,
      lineAmount: l.lineAmount ?? 0,
      category: l.category ?? null,
      isEditable: l.isEditable ?? 1,
      isDeleted: l.isDeleted ?? 'false',
      branchId: payload.branchId,
    }))
    const expenseClaimLinesJson = JSON.stringify(linesJson)
    formData.append('expenseClaimLinesJson', expenseClaimLinesJson)

    // Files: one per line, indexed keys must align with JSON array order
    ;(payload.expenseClaimD ?? []).forEach((_line, index) => {
      const file = lineFiles[index]
      if (file) {
        formData.append(`ExpenseClaimD[${index}].AttachmentFile`, file, file.name)
      }
    })

    return formData
  }

  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<ExpenseClaimListResponse> {
    const url = endpoints.expenseClaim.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<ExpenseClaimListResponse> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (raw && typeof raw === 'object') {
      const arr = Array.isArray(raw.data) ? raw.data : Array.isArray(raw) ? raw : []
      return {
        totalRecords: raw.totalRecords ?? payload.totalRecords ?? arr.length ?? 0,
        pageNumber: raw.pageNumber ?? payload.pageNumber ?? pageNumber,
        pageSize: raw.pageSize ?? payload.pageSize ?? pageSize,
        data: (arr as any[]).map(normalizeListItem),
      }
    }
    return { totalRecords: 0, pageNumber, pageSize, data: [] }
  }

  async getById(id: number, branchId: number): Promise<ExpenseClaimVm | null> {
    const url = endpoints.expenseClaim.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    const obj = Array.isArray(raw) ? raw[0] : raw
    if (!obj || typeof obj !== 'object') return null
    return this.normalizeVm(obj, branchId)
  }

  async getLinesById(id: number, branchId: number): Promise<ExpenseClaimLineVm[]> {
    const url = endpoints.expenseClaim.getLinesById(id, branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map((line: any) => ({
      id: line.id ?? line.Id,
      expenseClaimId: line.expenseClaimId ?? line.ExpenseClaimId,
      invoiceDate: line.invoiceDate ?? line.InvoiceDate ?? '',
      expenseTypeId: line.expenseTypeId ?? line.ExpenseTypeId ?? 0,
      description: line.description ?? line.Description ?? '',
      taxableAmount: line.taxableAmount ?? line.TaxableAmount ?? 0,
      taxRateId: line.taxRateId ?? line.TaxRateId ?? 0,
      taxPercent: line.taxPercent ?? line.TaxPercent ?? 0,
      taxAmount: line.taxAmount ?? line.TaxAmount ?? 0,
      amount: line.amount ?? line.Amount ?? 0,
      attachmentUrl: line.attachmentUrl ?? line.AttachmentUrl,
    }))
  }

  async getDepartments(_branchId: number): Promise<ExpenseClaimLookupItem[]> {
    // Use Employee/GetDepartment (no parameters) as in other modules
    const url = endpoints.employeeLookups.departments
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map(normalizeLookup)
  }

  async getEmployees(branchId: number): Promise<ExpenseClaimLookupItem[]> {
    const url = endpoints.expenseClaim.getEmployees(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map((e: any) => {
      const fullName =
        e.fullName ??
        e.FullName ??
        e.employeeName ??
        e.EmployeeName ??
        [e.empFirstNameEn ?? e.EmpFirstNameEn, e.empFatherNameEn ?? e.EmpFatherNameEn, e.empSecondNameEn ?? e.EmpSecondNameEn, e.empLastNameEn ?? e.EmpLastNameEn]
          .filter(Boolean)
          .join(' ')
          .trim()
      return {
        id: e.id ?? e.Id ?? e.employeeId ?? e.EmployeeId ?? e.empId ?? e.EmpId ?? 0,
        name: fullName || (e.name ?? e.Name),
        nameEn: e.nameEn ?? e.NameEn,
        nameAr: e.nameAr ?? e.NameAr,
        lookupNameEn: e.lookupNameEn ?? e.LookupNameEn,
        lookupNameAr: e.lookupNameAr ?? e.LookupNameAr,
        departmentId: e.empDepartmetId ?? e.EmpDepartmetId ?? e.departmentId ?? e.DepartmentId,
      }
    })
  }

  async getTaxRates(branchId: number): Promise<ExpenseClaimLookupItem[]> {
    const url = endpoints.expenseClaim.getTaxRates(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map(normalizeLookup)
  }

  /**
   * GET /api/ExpenseClaimRequest/GetExpenseTypes — no branch segment (Swagger).
   * branchId kept for call-site compatibility; unused in URL.
   */
  async getExpenseTypes(_branchId?: number): Promise<ExpenseClaimLookupItem[]> {
    const url = endpoints.expenseClaim.getExpenseTypes
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list
      .filter((item: any) => {
        const active = item.isActive ?? item.IsActive
        if (active === undefined || active === null) return true
        return active === 1 || active === true
      })
      .map((item: any) => {
        const en = item.lookupNameEn ?? item.LookupNameEn ?? item.name ?? item.Name ?? ''
        const ar = item.lookupNameAr ?? item.LookupNameAr ?? ''
        const id = item.id ?? item.Id ?? 0
        return {
          id,
          name: en || ar || `Type ${id}`,
          nameEn: en,
          nameAr: ar,
          lookupNameEn: item.lookupNameEn ?? item.LookupNameEn,
          lookupNameAr: item.lookupNameAr ?? item.LookupNameAr,
        }
      })
  }

  async save(payload: ExpenseClaimSavePayload, lineFiles: ExpenseClaimLineFiles = {}): Promise<any> {
    const expenseClaimLinesJson = JSON.stringify(
      (payload.expenseClaimD ?? []).map((l) => ({
        lineId: l.lineId ?? 0,
        invoiceDate: l.invoiceDate ?? null,
        description: l.description ?? '',
        price: l.price ?? 0,
        vatPercent: l.vatPercent ?? 0,
        vatAmount: l.vatAmount ?? 0,
        lineAmount: l.lineAmount ?? 0,
        category: l.category ?? null,
        isEditable: l.isEditable ?? 1,
        isDeleted: l.isDeleted ?? 'false',
        branchId: payload.branchId,
      }))
    )
    this.logOutboundPayload('save', payload, expenseClaimLinesJson, lineFiles)
    const formData = this.buildSaveFormData(payload, lineFiles)
    this.debugFormData('SaveExpenseClaim', formData)
    const response = await apiClient.post(endpoints.expenseClaim.save, formData)
    return response.data
  }

  async update(payload: ExpenseClaimSavePayload, lineFiles: ExpenseClaimLineFiles = {}): Promise<any> {
    const expenseClaimLinesJson = JSON.stringify(
      (payload.expenseClaimD ?? []).map((l) => ({
        lineId: l.lineId ?? 0,
        invoiceDate: l.invoiceDate ?? null,
        description: l.description ?? '',
        price: l.price ?? 0,
        vatPercent: l.vatPercent ?? 0,
        vatAmount: l.vatAmount ?? 0,
        lineAmount: l.lineAmount ?? 0,
        category: l.category ?? null,
        isEditable: l.isEditable ?? 1,
        isDeleted: l.isDeleted ?? 'false',
        branchId: payload.branchId,
      }))
    )
    this.logOutboundPayload('update', payload, expenseClaimLinesJson, lineFiles)
    const formData = this.buildSaveFormData(payload, lineFiles)
    this.debugFormData('UpdateExpenseClaim', formData)
    // Backend expects multipart/form-data; use POST (Swagger)
    const response = await apiClient.post(endpoints.expenseClaim.update, formData)
    return response.data
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.expenseClaim.delete(id)
    const response = await apiClient.delete(url)
    return response.data
  }

  private normalizeVm(raw: any, branchId: number): ExpenseClaimVm {
    return {
      id: raw.id ?? raw.Id ?? 0,
      branchId: raw.branchId ?? raw.BranchId ?? branchId,
      departmentId: raw.departmentId ?? raw.DepartmentId ?? 0,
      employeeId: raw.employeeId ?? raw.EmployeeId ?? raw.empId ?? raw.EmpId ?? 0,
      amount: raw.amount ?? raw.Amount ?? 0,
      details: raw.details ?? raw.Details ?? raw.detail ?? raw.Detail ?? '',
      claimDate: raw.claimDate ?? raw.ClaimDate ?? '',
      claimTypeId: raw.claimTypeId ?? raw.ClaimTypeId,
      claimType: raw.claimType ?? raw.ClaimType,
      expenseDate: raw.expenseDate ?? raw.ExpenseDate ?? raw.claimDate ?? raw.ClaimDate ?? '',
      expenseClaimLines: Array.isArray(raw.expenseClaimLines ?? raw.ExpenseClaimLines)
        ? (raw.expenseClaimLines ?? raw.ExpenseClaimLines).map((l: any) => ({
            id: l.id ?? l.Id,
            expenseClaimId: l.expenseClaimId ?? l.ExpenseClaimId,
            invoiceDate: l.invoiceDate ?? l.InvoiceDate ?? '',
            expenseTypeId: l.expenseTypeId ?? l.ExpenseTypeId ?? 0,
            description: l.description ?? l.Description ?? '',
            taxableAmount: l.taxableAmount ?? l.TaxableAmount ?? 0,
            taxRateId: l.taxRateId ?? l.TaxRateId ?? 0,
            taxPercent: l.taxPercent ?? l.TaxPercent ?? 0,
            taxAmount: l.taxAmount ?? l.TaxAmount ?? 0,
            amount: l.amount ?? l.Amount ?? 0,
            attachmentUrl: l.attachmentUrl ?? l.AttachmentUrl,
          }))
        : [],
      subTotal: raw.subTotal ?? raw.SubTotal,
      totalVat: raw.totalVat ?? raw.TotalVat ?? raw.totalTax ?? raw.TotalTax,
      total: raw.total ?? raw.Total,
    }
  }
}

export const expenseClaimService = new ExpenseClaimService()
